'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { EmblaCarouselType, EmblaEventType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Article } from '@repo/types';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@repo/ui/components/ui/carousel';

import { ArticleCarouselItem } from './article-carousel-item';

const TWEEN_X_FACTOR_BASE = 0.1;
const TWEEN_Y_FACTOR_BASE = 0.2;

const carouselOptions: Parameters<typeof useEmblaCarousel>[0] = {
  align: 'center',
  containScroll: false,
};

const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max);

const EmptyCarouselItem: React.FC = () => (
  <div className="basis-[240px] @cred-md:basis-[290px] @cred-lg:basis-[325px] @cred-xl:basis-[420px]">
    <div className="flex min-h-[104px] w-full items-center justify-center p-5 @cred-md:min-h-[136px] @cred-lg:min-h-[144px] @cred-xl:min-h-[193px]">
      <span className="text-sm @cred-md:text-base @cred-xl:text-lg">
        No article found
      </span>
    </div>
  </div>
);

interface ArticlesCarouselProps {
  articleMetrics?: Article;
  isHackerNews: boolean;
  shouldOpenComments?: boolean;
}

export const PureArticlesCarousel: React.FC<ArticlesCarouselProps> = ({
  articleMetrics,
  isHackerNews,
  shouldOpenComments = false,
}) => {
  const [selected, setSelected] = useState<number>(0);

  const articles = useMemo(() => {
    if (articleMetrics) {
      return [articleMetrics];
    }
    return [];
  }, [articleMetrics]);

  return (
    <div className="w-full flex items-center justify-center">
      {articles.length === 0 ? (
        <EmptyCarouselItem />
      ) : (
        articles.map((article, index) => (
          <div
            key={article.id}
            className="basis-[240px] @cred-md:basis-[290px] @cred-lg:basis-[325px] @cred-xl:basis-[420px]"
          >
            <ArticleCarouselItem
              index={index}
              article={article}
              selected={selected}
              onSelect={setSelected}
              metrics={articleMetrics?.metrics}
              isHackerNews={isHackerNews}
              shouldOpenComments={shouldOpenComments}
            />
          </div>
        ))
      )}
    </div>
  );
};

const DeprecatedEmptyCarouselItem: React.FC = () => (
  <CarouselItem className="basis-[221px] pl-2.5 @cred-md:basis-[275px] @cred-md:pl-3 @cred-lg:basis-[299px] @cred-lg:pl-3.5 @cred-xl:basis-[393px] @cred-xl:pl-5">
    <div className="flex min-h-[104px] w-full items-center justify-center p-5 @cred-md:min-h-[136px] @cred-lg:min-h-[144px] @cred-xl:min-h-[193px]">
      <span className="text-sm @cred-md:text-base @cred-xl:text-lg">
        No articles found
      </span>
    </div>
  </CarouselItem>
);

export const DeprecatedPureArticlesCarousel: React.FC<
  ArticlesCarouselProps
> = ({ articleMetrics, isHackerNews, shouldOpenComments = false }) => {
  const [emblaApi, setEmblaApi] = useState<CarouselApi>();
  const [selected, setSelected] = useState<number>(0);

  const tweenXFactor = useRef(0);
  const tweenYFactor = useRef(0);
  const tweenNodes = useRef<HTMLElement[]>([]);

  const articles = useMemo(() => {
    if (articleMetrics) {
      return [articleMetrics];
    }
    return [];
  }, [articleMetrics]);

  const hasMultipleArticles = articles.length > 1;

  const setTweenNodes = useCallback((emblaApi: EmblaCarouselType): void => {
    tweenNodes.current = emblaApi.slideNodes().map((slideNode, index) => {
      return slideNode.querySelector(
        `#embla__slide__content__${index}`,
      ) as HTMLElement;
    });
  }, []);

  const setTweenFactor = useCallback((emblaApi: EmblaCarouselType) => {
    const snapListLength = emblaApi.scrollSnapList().length;
    tweenXFactor.current = TWEEN_X_FACTOR_BASE * snapListLength;
    tweenYFactor.current = TWEEN_Y_FACTOR_BASE * snapListLength;
  }, []);

  const tweenScale = useCallback(
    (emblaApi: EmblaCarouselType, eventName?: EmblaEventType) => {
      const engine = emblaApi.internalEngine();
      const scrollProgress = emblaApi.scrollProgress();
      const slidesInView = emblaApi.slidesInView();
      const isScrollEvent = eventName === 'scroll';

      emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.slideRegistry[snapIndex];

        slidesInSnap?.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress);
                }
                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress);
                }
              }
            });
          }

          const tweenXValue = 1 - Math.abs(diffToTarget * tweenXFactor.current);
          const tweenYValue = 1 - Math.abs(diffToTarget * tweenYFactor.current);
          const scaleX = numberWithinRange(tweenXValue, 0, 1).toString();
          const scaleY = numberWithinRange(tweenYValue, 0, 1).toString();
          const tweenNode = tweenNodes.current[slideIndex];
          if (tweenNode) {
            tweenNode.style.transform = `scaleX(${scaleX}) scaleY(${scaleY})`;
          }
        });
      });
    },
    [],
  );

  const handleSelect = useCallback(
    (index: number) => {
      setSelected(index);
      emblaApi?.scrollTo(index);
    },
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;

    setTweenNodes(emblaApi);
    setTweenFactor(emblaApi);
    tweenScale(emblaApi);

    emblaApi
      .on('reInit', setTweenNodes)
      .on('reInit', setTweenFactor)
      .on('reInit', tweenScale)
      .on('scroll', tweenScale)
      .on('slideFocus', tweenScale);

    return () => {
      emblaApi
        .off('reInit', setTweenNodes)
        .off('reInit', setTweenFactor)
        .off('reInit', tweenScale)
        .off('scroll', tweenScale)
        .off('slideFocus', tweenScale);
    };
  }, [emblaApi, setTweenNodes, setTweenFactor, tweenScale]);

  return (
    <Carousel opts={carouselOptions} setApi={setEmblaApi} className="w-full">
      <CarouselContent className="-ml-2.5 items-center @cred-md:-ml-3 @cred-lg:-ml-3.5 @cred-xl:-ml-5">
        {articles.length === 0 ? (
          <DeprecatedEmptyCarouselItem />
        ) : (
          articles.map((article, index) => (
            <CarouselItem
              key={article.id}
              className="basis-[240px] pl-2.5 @cred-md:basis-[290px] @cred-md:pl-3 @cred-lg:basis-[325px] @cred-lg:pl-3.5 @cred-xl:basis-[420px] @cred-xl:pl-5"
            >
              <ArticleCarouselItem
                index={index}
                article={article}
                selected={selected}
                onSelect={handleSelect}
                metrics={articleMetrics?.metrics}
                isHackerNews={isHackerNews}
                shouldOpenComments={shouldOpenComments}
              />
            </CarouselItem>
          ))
        )}
      </CarouselContent>
      {hasMultipleArticles && (
        <>
          <CarouselPrevious
            variant="ghost"
            className="left-5 z-20 size-6 rounded-none hover:bg-transparent @cred-md:left-6 @cred-lg:left-7 @cred-xl:left-8 [&_svg]:size-5 @cred-md:[&_svg]:size-[22.5px] @cred-lg:[&_svg]:size-[25.3px] @cred-xl:[&_svg]:size-6"
          >
            <ChevronLeft />
            <span className="sr-only">Previous slide</span>
          </CarouselPrevious>
          <CarouselNext
            variant="ghost"
            className="right-5 z-20 size-6 rounded-none hover:bg-transparent @cred-md:right-6 @cred-lg:right-7 @cred-xl:right-8 [&_svg]:size-5 @cred-md:[&_svg]:size-[22.5px] @cred-lg:[&_svg]:size-[25.3px] @cred-xl:[&_svg]:size-6"
          >
            <ChevronRight />
            <span className="sr-only">Next slide</span>
          </CarouselNext>
        </>
      )}
    </Carousel>
  );
};
